import React, { useEffect, useRef, useState } from 'react';
import {
  TextField,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Typography
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { loadGoogleMapsAPI } from './utils/googleMapsLoader';

/**
 * Google Places Autocomplete component for location search
 * Requires Google Maps JavaScript API with Places library
 */
const GooglePlacesAutocomplete = ({
  value = '',
  onChange,
  onPlaceSelect,
  label = 'Search Location',
  placeholder = 'Enter address or location name...',
  error = false,
  helperText = '',
  fullWidth = true,
  variant = 'outlined',
  disabled = false,
  ...textFieldProps
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Fix z-index for Google Places dropdown to appear above MUI Dialog
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        z-index: 1500 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const ensureLoadedAndInit = async () => {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          setLoading(true);
          await loadGoogleMapsAPI();
        }

        if (cancelled) return;

        // Initialize autocomplete with a delay to ensure DOM is ready
        const initializeAutocomplete = () => {
          if (cancelled) return;
          if (inputRef.current && !autocompleteRef.current) {
            try {
              const inputElement = inputRef.current.querySelector('input') || inputRef.current;
              if (!inputElement || inputElement.tagName !== 'INPUT') {
                console.warn('Could not find valid input element for Google Places Autocomplete');
                setApiError('Unable to initialize location search. Please refresh the page.');
                return;
              }

              autocompleteRef.current = new window.google.maps.places.Autocomplete(
                inputElement,
                {
                  types: ['establishment', 'geocode'],
                  fields: [
                    'place_id',
                    'name',
                    'formatted_address',
                    'address_components',
                    'geometry',
                    'types',
                    'photos',
                    'rating',
                    'website',
                    'formatted_phone_number'
                  ]
                }
              );
              autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
              setApiError(null);
            } catch (error) {
              console.error('Error initializing Google Places Autocomplete:', error);
              setApiError('Failed to initialize location search. Please try again.');
            }
          }
        };

        setTimeout(initializeAutocomplete, 100);
      } catch (e) {
        console.error('Google Maps API load failed:', e);
        setApiError('Google Maps API not loaded. Check API key, enable Places library, and allow this origin in key restrictions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    ensureLoadedAndInit();

    return () => {
      cancelled = true;
      if (autocompleteRef.current && window.google?.maps?.event?.clearInstanceListeners) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    setLoading(true);
    const place = autocompleteRef.current.getPlace();

    if (!place.geometry) {
      setApiError('No location data found for this place. Please try a different search.');
      setLoading(false);
      return;
    }

    // Format the place data
    const formattedPlace = {
      placeId: place.place_id,
      name: place.name || '',
      address: place.formatted_address || '',
      coordinates: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      },
      addressComponents: place.address_components || [],
      types: place.types || [],
      photos: place.photos || [],
      rating: place.rating || null,
      website: place.website || '',
      phone: place.formatted_phone_number || ''
    };

    setSelectedPlace(formattedPlace);
    setApiError(null);
    setLoading(false);

    // Call the callbacks
    if (onChange) {
      onChange(place.formatted_address || '');
    }
    if (onPlaceSelect) {
      onPlaceSelect(formattedPlace);
    }
  };

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    if (onChange) {
      onChange(newValue);
    }
    // Clear selected place if user is typing
    if (selectedPlace && newValue !== selectedPlace.address) {
      setSelectedPlace(null);
    }
  };

  const getAddressComponents = (place) => {
    if (!place || !place.addressComponents) return [];
    
    return place.addressComponents.map(component => ({
      type: component.types[0],
      longName: component.long_name,
      shortName: component.short_name
    }));
  };

  return (
    <Box>
      <TextField
        {...textFieldProps}
        inputRef={inputRef}
        value={value}
        onChange={handleInputChange}
        label={label}
        placeholder={placeholder}
        error={error || !!apiError}
        helperText={apiError || helperText}
        fullWidth={fullWidth}
        variant={variant}
        disabled={disabled || loading}
        InputProps={{
          endAdornment: loading ? (
            <CircularProgress size={20} />
          ) : (
            <LocationOnIcon color={selectedPlace ? 'primary' : 'disabled'} />
          ),
          ...textFieldProps.InputProps
        }}
      />

      {apiError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {apiError}
        </Alert>
      )}


    </Box>
  );
};

export default GooglePlacesAutocomplete;
